import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSupabase } from '../../contexts/SupabaseContext';
import { PieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../../theme';
import { useTheme } from '@mui/material';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';

// Colors for the pie chart - matching AssetClassDetails
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f', 
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b'
];

const CashAndLiquid = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { supabase, user } = useSupabase();
  const [loading, setLoading] = useState(true);
  const tour = useTour('inv-cash-and-liquid', { ready: !loading });
  const [investments, setInvestments] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });

  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });

    const sortedData = [...investments].sort((a, b) => {
      if (key === 'name') {
        return direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (key === 'value') {
        return direction === 'asc' 
          ? a.value - b.value
          : b.value - a.value;
      } else if (key === 'allocation') {
        const aAllocation = totalValue > 0 ? (a.value / totalValue) * 100 : 0;
        const bAllocation = totalValue > 0 ? (b.value / totalValue) * 100 : 0;
        return direction === 'asc' 
          ? aAllocation - bAllocation
          : bAllocation - aAllocation;
      }
      return 0;
    });

    setInvestments(sortedData);
  };

  const fetchInvestments = useCallback(async () => {
    if (!user) {
      console.log('No user found, cannot fetch investments');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching investments for user:', user.id);

      // Fetch Savings Bank total
      let savingsTotal = 0;
      try {
      const { data: savingsData, error: savingsError } = await supabase
        .from('wallets')
        .select('balance')
        .eq('user_id', user.id)
        .eq('type', 'bank')
        .not('is_archived', 'is', true); // hide archived bank accounts

      if (savingsError) {
        console.error('Error fetching savings bank data:', savingsError);
        } else {
          savingsTotal = savingsData.reduce((sum, item) => sum + (Number(item.balance) || 0), 0);
          console.log('Savings Bank total:', savingsTotal);
      }
      } catch (err) {
        console.error('Exception fetching savings bank:', err);
      }

      // Fetch Liquid ETF investments
      let liquidETFTotal = 0;
      try {
        const { data: liquidETFData, error: liquidETFError } = await supabase
          .from('liquid_etf_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (liquidETFError) {
          console.error('Error fetching liquid ETF data:', liquidETFError);
        } else {
          liquidETFTotal = liquidETFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Liquid ETF total:', liquidETFTotal);
        }
      } catch (err) {
        console.error('Exception fetching liquid ETF:', err);
      }

      // Fetch Mutual Fund investments
      let mutualFundTotal = 0;
      try {
        const { data: mutualFundData, error: mutualFundError } = await supabase
          .from('mutual_fund_investments')
          .select('amount_invested')
          .eq('user_id', user.id);

        if (mutualFundError) {
          console.error('Error fetching mutual fund data:', mutualFundError);
        } else {
          mutualFundTotal = mutualFundData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
          console.log('Mutual Fund total:', mutualFundTotal);
        }
      } catch (err) {
        console.error('Exception fetching mutual funds:', err);
      }

      // Format data for display
      const investmentsList = [
        {
          id: 'savings-bank',
          name: 'Savings Bank',
          value: savingsTotal,
          path: '/investments/cash-and-liquid/savings-bank'
        },
        {
          id: 'liquid-etf',
          name: 'Liquid ETF',
          value: liquidETFTotal,
          path: '/investments/cash-and-liquid/liquid-etf'
        },
        {
          id: 'mutual-fund',
          name: 'Mutual Fund',
          value: mutualFundTotal,
          path: '/investments/cash-and-liquid/mutual-fund'
        }
      ];

      // Sort by value (descending)
      investmentsList.sort((a, b) => b.value - a.value);

      setInvestments(investmentsList);
      const total = investmentsList.reduce((sum, item) => sum + item.value, 0);
      setTotalValue(total);
      console.log('Total value of all investments:', total);

    } catch (error) {
      console.error('Error in fetchInvestments:', error);
      setInvestments([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    fetchInvestments();
  }, [fetchInvestments]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header with back button - matching AssetClassDetails style */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton
          onClick={() => navigate('/investments')}
          sx={{
            mr: 2,
            color: theme.palette.grey[500],
            '&:hover': { color: theme.palette.primary.main }
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box data-tour="inv-class-header">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Cash And Liquid
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Total: {new Intl.NumberFormat('en-IN', {
              style: 'currency',
              currency: 'INR',
              maximumFractionDigits: 0
            }).format(totalValue)}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
          <TourButton onClick={tour.start} />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Left side - Table with AssetClassDetails styling */}
          <Grid item xs={12} md={8}>
            <Paper
              data-tour="inv-class-products"
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                backdropFilter: 'blur(20px)',
                boxShadow: 'var(--fx-card-shadow)'
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Products
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        onClick={() => handleSort('name')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Product Name
                        {sortConfig.key === 'name' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('value')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Value
                        {sortConfig.key === 'value' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell 
                        align="right"
                        onClick={() => handleSort('allocation')}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        Allocation
                        {sortConfig.key === 'allocation' && (
                          <Box component="span" sx={{ ml: 0.5, color: 'primary.main' }}>
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.map((investment, index) => (
                      <TableRow
                        key={investment.id}
                        onClick={() => navigate(investment.path)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: COLORS[index % COLORS.length],
                                mr: 2
                              }}
                            />
                            {investment.name}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {new Intl.NumberFormat('en-IN', {
                            style: 'currency',
                            currency: 'INR',
                            maximumFractionDigits: 0
                          }).format(investment.value)}
                        </TableCell>
                        <TableCell align="right">
                          {totalValue > 0 ? ((investment.value / totalValue) * 100).toFixed(1) : '0'}%
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Add Total Row */}
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          maximumFractionDigits: 0,
                        }).format(totalValue)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        100%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Distribution Chart */}
          <Grid item xs={12} md={4}>
            <Paper
              data-tour="inv-class-chart" sx={{ 
              height: '100%', 
              borderRadius: 2, 
              p: 2 
            }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Distribution</Typography>
              {investments.length > 0 && (
                <PieChart colors={chartPalette}
                  series={[{
                    data: investments.map(item => ({
                      id: item.id,
                      value: item.value,
                      label: item.name
                    })),
                    highlightScope: { faded: 'global', highlighted: 'item' },
                    faded: { innerRadius: 30, additionalRadius: -30 },
                  }]}
                  height={250}
                  legend={{
                    hidden: true
                  }}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
      <GuidedTour steps={TOURS['inv-cash-and-liquid']} open={tour.open} onClose={tour.close} />
    </Box>
  );
};

export default CashAndLiquid;